import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import { CanSeeModulesMarketplaceDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'
import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import { ProductUpdate } from 'modules/product_updates/types'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

type Props = {
  productUpdate: ProductUpdate
  isProductUpdateAcquired: boolean
  source: string
}

const isValidAvailableModule = (value: string | undefined | null): value is AvailableModules => {
  if (!value) return false
  return Object.values(AvailableModules).some((module) => module === value)
}

export const ModuleRelatedModulesCard = ({
  productUpdate,
  isProductUpdateAcquired,
  source,
}: Props) => {
  const {
    access: { locale: user_locale },
  } = useCurrentSession()
  const { data: policies } = useQuery(CanSeeModulesMarketplaceDocument)
  const canSeeMarketplaceModules = policies?.modules._policies.moduleManageModule.can

  const datoFormatLocale = getDatoLocale(user_locale)
  const { data: moduleData } = useGetDatoModules({
    locale: datoFormatLocale,
    slug: productUpdate?.productModule.urlIdentifier,
  })

  const effectiveModule1 = productUpdate?.productModule.effectiveModule1
  const effectiveModule2 = productUpdate?.productModule.effectiveModule2
  const effectiveModule3 = productUpdate?.productModule.effectiveModule3

  const isModule1Acquired = useCheckModuleAcquired(effectiveModule1?.chargebeeModuleName || '')
  const isModule2Acquired = useCheckModuleAcquired(effectiveModule2?.chargebeeModuleName || '')
  const isModule3Acquired = useCheckModuleAcquired(effectiveModule3?.chargebeeModuleName || '')

  if (productUpdate.productModule.urlIdentifier === 'factorial-core') return null

  const relatedModules = [effectiveModule1, effectiveModule2, effectiveModule3]
  if (relatedModules.some((module) => !module) && isProductUpdateAcquired) return null

  let recommendedModule = null
  if (!isModule1Acquired && effectiveModule1) {
    recommendedModule = effectiveModule1
  }
  if (!isModule2Acquired && effectiveModule2) {
    recommendedModule = effectiveModule2
  }
  if (!isModule3Acquired && effectiveModule3) {
    recommendedModule = effectiveModule3
  }
  if (!isProductUpdateAcquired) {
    recommendedModule = moduleData[0]
  }

  if (
    (!recommendedModule && !productUpdate && isProductUpdateAcquired) ||
    !canSeeMarketplaceModules
  )
    return null

  if (isProductUpdateAcquired && isModule1Acquired && isModule2Acquired && isModule3Acquired) {
    return null
  }

  const validModuleName = isValidAvailableModule(recommendedModule?.chargebeeModuleName)
    ? recommendedModule.chargebeeModuleName
    : null

  return (
    <Box paddingY="s12">
      <Divider />
      <Box paddingY="s24">
        <Text size="400" weight="semibold" color="black">
          {isProductUpdateAcquired
            ? i18n.t('product_updates.detail_page.module_related_updates.title_acquired')
            : i18n.t('product_updates.detail_page.module_related_updates.title')}
        </Text>
        <Box className="text-lg text-f1-foreground-secondary" width="s400">
          {i18n.t('product_updates.detail_page.module_related_updates.description')}
        </Box>
      </Box>
      {recommendedModule && validModuleName && (
        <UpsellingKit.ProductCard
          moduleRequested={validModuleName}
          title={recommendedModule.name || undefined}
          description={recommendedModule.shortDescription || undefined}
          requestSource={source}
          requestReason={ValidReasons.MissingAddon}
          dismissible={false}
        />
      )}
    </Box>
  )
}
